<template>
  <div class="">
    <div v-if="!displayName" class="w-100 mb-3">
      <div class="name-placeholder d-inline-block mt-2"/>
      <loading-spinner class="mt-2"/>
    </div>
    <div v-else class="w-100">
      <div v-if="!nameEdit" class="name-edit">
        <component
            class="mr-1 d-inline-block"
            :is="htmlTagForDisplay">
          <icon v-if="icon" :type="icon" class="mr-2" size="0.9"/>
          {{ displayName }}
        </component>
        <div
            v-if="editable"
             class="edit-icon d-inline-block mb-1 lighter clickable"
             @click.prevent="editname">
          <icon :size="editIconSize" type="pencil-alt"/>
        </div>
      </div>
      <div v-else>
        <div class="vform-label white">{{ $t('displayName') }}</div>
        <input ref="displayName"
               v-model="updateDisplayName"
               :class="['form-text form-text-dark', error ? 'form-group--error' : '']"
               type="text"
               @input="delayTouch($v.updateDisplayName)"
               @keyup.enter="updateContent"
               @keyup.esc="cancelEditing"
        >
        <div v-if="error" class="form-error">{{ $t(error) }}</div>
        <div v-if="$v.updateDisplayName.$dirty && !$v.updateDisplayName.required && $v.updateDisplayName.$error"
             class="form-error">
          {{ $t('errors.required') }}
        </div>
        <div v-if="$v.updateDisplayName.$dirty && !$v.updateDisplayName.alphaNumSpace" class="form-error">
          {{ $t('errors.alphaNumSpaceOnly') }}
        </div>

        <div v-if="$v.updateDisplayName.$dirty && !$v.updateDisplayName.maxLength" class="form-error">
          {{ $t('errors.atMostCharacters', {num: maxNameLength}) }}
        </div>

        <div v-if="$v.updateDisplayName.$dirty && !$v.updateDisplayName.minLength" class="form-error">
          {{ $t('errors.atLeastCharacters', {num: 3}) }}
        </div>

        <div class="vform-label white mt-2">{{ $t('Name') }}</div>
        <input ref="name"
               v-model="updateName"
               :class="['form-text form-text-dark', error ? 'form-group--error' : '']"
               type="text"
               @input="delayTouch($v.updateName)"
               @keyup.enter="updateContent"
               @keyup.esc="cancelEditing"
        >
        <div v-if="$v.updateName.$dirty && !$v.updateName.isUnique" class="bg-dark p-1 mb-1">
          {{ $t('Suggestion') }}: {{ proposedNewName }}
        </div>
        <div v-if="error" class="form-error">{{ $t(error) }}</div>
        <div v-if="$v.updateName.$dirty && !$v.updateName.required && $v.updateName.$error" class="form-error">
          {{ $t('errors.required') }}
        </div>
        <div v-if="$v.updateName.$dirty && !$v.updateName.alphaNumSpace" class="form-error">
          {{ $t('errors.alphaNumSpaceOnly') }}
        </div>

        <div v-if="$v.updateName.$dirty && !$v.updateName.maxLength" class="form-error">
          {{ $t('errors.atMostCharacters', {num: maxNameLength}) }}
        </div>

        <div v-if="$v.updateName.$dirty && !$v.updateName.minLength" class="form-error">
          {{ $t('errors.atLeastCharacters', {num: 3}) }}
        </div>
        <div v-if="$v.updateName.$dirty && !$v.updateName.isUnique" class="form-error">
          {{ $t('errors.objectNameAlreadyExists') }}
        </div>

        <div class="edit-trigger d-inline-block mr-1 mt-1 clickable" @click="cancelEditing">
          <icon type="xmark" size="0.9"/>
        </div>
        <div v-if="enableSaving" class="edit-trigger mt-1 d-inline-block clickable" @click="updateContent">
          <icon type="save" size="0.9"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from "@/components/Icon";
import {required, minLength, maxLength} from 'vuelidate/lib/validators';
import {alphaNumSpace} from '../customValidators';
import VuelidateMixin from "@/components/mixins/VuelidateMixin.js";
import LoadingSpinner from "./LoadingSpinner";

export default {
  name: "NameDisplayEditBlock",
  components: {
    Icon,
    LoadingSpinner
  },
  mixins: [VuelidateMixin],
  props: {
    name: {type: String, default: ''},
    displayName: {type: String, default: ''},
    id: {type: String, required: true},
    cid: {type: String, default: null},
    storeName: {type: String, required: true},
    maxNameLength: {type: Number, default: 127},
    organizationId: {type: String, default: ''}, //necessary in most cases /except users
    icon: {type: String, default: null},
    editable: {type: Boolean, default: true},
    checkForUniqueName: {type: Boolean, default: true},
    htmlTagForDisplay: {type: String, default: 'h1'},
    editIconSize: {type: Number, default: 1.1},
  },
  data() {
    return {
      nameEdit: false,
      error: null,
      updateDisplayName: null,
      updateName: null,
      enableSaving: true,
      proposedNewName: null
    };
  },
  validations() {
    return {
    updateDisplayName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(this.maxNameLength),
      alphaNumSpace
    },
    updateName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(this.maxNameLength),
      alphaNumSpace,
      async isUnique(value) {
        if(!this.checkForUniqueName) {
          return true;
        }
        // standalone validator ideally should not assume a field is required
        if (value === '' || value === this.name) {
          return true;
        }
        let bool = true;
        let args = {value: value, name: value};
        if (this.organizationId) {
          args.organizationId = this.organizationId;
        }
        await this.$store.dispatch('checkIf' + this.storeName + 'NameExists', args).then(data => {
          bool = data.length === 0;
        });
        if (!bool) {
          await this.proposeNewName(value);
        }
        return bool;
      },
    }
    };
  },
  watch: {
    displayName(val) {
      if (val) {
        this.setValue();
      }
    },
    updateDisplayName() {
      this.error = null;
    },
    '$v.$invalid': function () {
      this.enableSaving = !this.$v.$invalid;
      this.$emit('enableSaving', !this.$v.$invalid);
    },
    nameEdit() {
      this.$emit('editing', this.nameEdit);
    }
  },
  mounted() {
    this.setValue();
  },
  methods: {
    async proposeNewName(name) {
      this.proposedNewName = "";
      let nameBase = name.split('-')[0];
      let counter = name.split('-')[1] || 0;
      let tmpString = name;
      let p;
      let args = {value: tmpString, name: tmpString};
      if (this.organizationId) {
        args.organizationId = this.organizationId;
      }
      /* eslint-disable no-constant-condition */
      while (true) {
        p = await this.$store.dispatch('checkIf' + this.storeName + 'NameExists', args);

        if (p.length) {
          counter++;
          tmpString = `${nameBase}-${counter}`;
          args.value = tmpString;
          args.name = tmpString;
        } else {
          break;
        }
      }
      this.proposedNewName = tmpString;
      return tmpString;
    },
    setValue() {
      this.updateDisplayName = this.displayName;
      this.updateName = this.name;
      this.proposedNewName = "";
    },
    editname: function () {
      this.nameEdit = true;
    },
    cancelEditing: function () {
      this.nameEdit = false;
      this.updateDisplayName = this.displayName;
    },
    updateContent() {
      if (this.enableSaving) {
        let args = {
          id: this.id,
          displayName: this.updateDisplayName,
          name: this.updateName,
        };
        if( this.cid ) {
          args.cid = this.cid;
        }
        this.error = null;
        console.log('calling', 'update' + this.storeName)
        this.$store.dispatch('update' + this.storeName, args)
            .then(data => {
              console.log(data);
              const {name, displayName} = data;
              this.updateName = name;
              this.updateDisplayName = displayName;
              if (!this.error) {
                this.nameEdit = false;
              }
              this.$emit('updated');
            }).catch(res => {
              console.log(res);
              if (res.response.errors.errorCode === 1003) {
                this.error = this.$t('errors.duplicateNameForAsset');
              } else {
                this.error = res.response.errors.message;
              }
        })
      } else if (this.updateDisplayName === this.displayName) {
        this.nameEdit = false;
      }
      this.proposedNewName = "";
    }
  },
}
</script>

<style lang="scss" scoped>
.name-edit {
  position: relative;
  word-break: break-all;
  display: inline-block;
  padding-right: 20px;
  .edit-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    margin-top: -2px;
    right: 0;
  }
}
.edit-trigger {
  cursor: pointer;
  background-color: var(--vform-editor-ui-quinary-color);
  border-radius: 3px;
  padding: 0 6px;
  transition: all 300ms ease;
  opacity: 0.6;
  &:hover {
    background-color: var(--vform-editor-ui-quinary-color);
    opacity: 1;
  }
}

.form-text {
  width: 100%;
}

.name-placeholder {
  width: 250px;
  height: 40px;
  background-color: var(--vform-editor-ui-secondary-color);
  filter: blur(1px);
}
</style>